// The historical dock as ROWS instead of a cluster: bubbles sorted by their count, the first at the left of the first row, running to the right and wrapping onto further rows.
// Pure (no DOM, no d3), so it can be tested on its own; clusterService.ts feeds it the dock's bubbles and glides each one to the slot returned here.
export type HistoricalRowsOrder = "desc" | "asc";

export interface RowItem {
  /** the category's name: unique among the dock's bubbles, and the tie-break so equal counts always keep the same order */
  id: string;
  /** how many sessions it holds: what the rows are sorted by */
  value: number;
  /** the width of the room it needs (its diameter); bubbles are laid out by this, so big ones are not squeezed against small ones */
  size: number;
  /** the width of room it takes along a row when that is MORE than its diameter (a label under it that is wider than it is); the bubble sits in the middle of it. Its height is still `size`. */
  room?: number;
  /** the height the bubble is DRAWN at, when that is not `size` (an icon is drawn at its own height; `size` is what collision and spacing use). Only read when the rows are bottom-aligned. */
  drawn?: number;
}

const roomOf = (item: RowItem): number => Math.max(item.size, item.room ?? 0);

export interface RowsBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface RowSlot {
  /** the bubble's CENTRE */
  x: number;
  y: number;
  row: number;
}

// More room between bubbles side by side than between rows: icons read as a row of separate things, not a strip (Chris, 2026-10-17: "space them out horizontally a bit more").
export const ROWS_GAP_X = 24;
export const ROWS_GAP_Y = 14;
export const ROWS_MARGIN = 14;
// Rows are never squeezed closer than this share of their natural spacing, however many there are: past it the dock simply runs a little below the band instead of overlapping itself.
const MIN_PITCH_SHARE = 0.55;

export const sortForRows = (items: RowItem[], order: HistoricalRowsOrder): RowItem[] =>
  items.slice().sort((a, b) => (order === "asc" ? a.value - b.value : b.value - a.value) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

/**
 * Flows the sorted bubbles left to right, starting a new row when the next one would pass the right edge. Each row is CENTRED across the box (a short last row sits in the middle too),
 * every row is as tall as its tallest bubble with the bubbles on its middle line (or, with `alignBottom`, STANDING on its bottom edge, so a big icon rises above its neighbours and the names hung under them share one level), and the whole block is CENTRED between the box's top and bottom, as the cluster was. When the rows
 * would be taller than the box, their spacing is tightened (down to MIN_PITCH_SHARE) and the block starts at the top.
 */
export const layoutHistoricalRows = (items: RowItem[], box: RowsBox, order: HistoricalRowsOrder, gapX: number = ROWS_GAP_X, gapY: number = ROWS_GAP_Y, margin: number = ROWS_MARGIN, alignBottom = false): Map<string, RowSlot> => {
  const slots = new Map<string, RowSlot>();
  const sorted = sortForRows(items, order);
  if (sorted.length === 0) {
    return slots;
  }
  const startX = box.left + margin;
  const limitX = box.right - margin;

  // 1. cut the sorted list into rows. At the panel's full width the rows come out lopsided (a row of seventeen and a row of one), so once the NUMBER of rows is known the wrap width is
  // brought in to the narrowest that still gives that many: the same rows, nearly even, still in the same order.
  const cut = (limit: number): RowItem[][] => {
    const out: RowItem[][] = [[]];
    let at = startX;
    for (const item of sorted) {
      const current = out[out.length - 1];
      if (current.length > 0 && at + roomOf(item) > limit) {
        out.push([]);
        at = startX;
      }
      out[out.length - 1].push(item);
      at += roomOf(item) + gapX;
    }

    return out;
  };
  let rows = cut(limitX);
  if (rows.length > 1) {
    const wanted = rows.length;
    let lo = Math.max(...sorted.map(roomOf)) + startX;   // no narrower than the biggest bubble (or its label)
    let hi = limitX;
    for (let step = 0; step < 24 && hi - lo > 0.5; step++) {
      const mid = (lo + hi) / 2;
      if (cut(mid).length <= wanted) hi = mid; else lo = mid;
    }
    rows = cut(hi);
  }

  // 2. each row's height, and how much the spacing between rows has to tighten to fit the box
  const heights = rows.map((r) => Math.max(...r.map((i) => i.size)));
  const natural = heights.reduce((total, h, i) => (i === 0 ? 0 : total + heights[i - 1] / 2 + h / 2 + gapY), 0);
  const avail = box.bottom - box.top - 2 * margin;
  const first = heights[0] / 2;
  const last = heights[heights.length - 1] / 2;
  const share = natural > 0 && first + natural + last > avail ? Math.max(MIN_PITCH_SHARE, (avail - first - last) / natural) : 1;
  const blockH = first + natural * share + last;

  // 3. place them: the block centred vertically, each row centred horizontally
  let y = box.top + margin + Math.max(0, (avail - blockH) / 2) + first;
  const mid = (box.left + box.right) / 2;
  rows.forEach((row, r) => {
    if (r > 0) {
      y += (heights[r - 1] / 2 + heights[r] / 2 + gapY) * share;
    }
    const rowW = row.reduce((w, item) => w + roomOf(item), 0) + gapX * (row.length - 1);
    let x = rowW <= limitX - startX ? mid - rowW / 2 : startX;   // (a row too wide for the box, a single huge bubble, starts at the margin)
    for (const item of row) {
      // bottom-aligned: every bubble's DRAWN lower edge lands on the row's bottom edge, so whatever hangs under it (its name) is at one height across the row
      const centre = alignBottom ? y + heights[r] / 2 - (item.drawn ?? item.size) / 2 : y;
      slots.set(item.id, { x: x + roomOf(item) / 2, y: centre, row: r });
      x += roomOf(item) + gapX;
    }
  });

  return slots;
};
